/* eslint-disable no-console */
import { useMutation } from '@apollo/client';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  ConversationCategory,
  ConversationStatus,
  Gender,
  GenerateSignalRAccessTokenDocument,
  PushType,
  SendTestNotificationDocument,
  SubscribeSignalRTagsDocument,
} from '../data/types/api/graphql';

export enum SignalRFilterKey {
  CHAT = 'CHAT',
  SCHEDULE = 'SCHEDULE',
  SCHEDULEDATE = 'SCHEDULEDATE',
  CONVERSATION = 'CONVERSATION',
  CONVERSATIONS = 'CONVERSATIONS',
  AGENDA = 'AGENDA',
}

export type SignalRFilter = {
  key: SignalRFilterKey;
  filter: (signalRMessage: BaseSignalRMessage, info?: string) => boolean;
};

export type BaseSignalRMessage = {
  type: PushType;
  isSilent: boolean;
  uniqueId?: string;
};

export type BaseLoginExpiredSignalRMessage = BaseSignalRMessage & {
  healthProfessionalId: string;
  pinCodeId?: string;
};

export type BaseHealthProfessionalSignalRMessage = BaseSignalRMessage & {
  healthProviderId: string;
  healthProfessionalId: string;
};

export type BaseConversationSignalRMessage =
  BaseHealthProfessionalSignalRMessage & {
    conversationId: string;
    conversation?: string;
  };

export type ConversationSignalRMessage = BaseConversationSignalRMessage & {
  status: ConversationStatus;
  healthProfessionalId: string;
  isNew: boolean;
  noReply: boolean;
  healthProfessionalInitials?: string;
  patientFirstName: string;
  patientLastName: string;
  patientGender: Gender;
  patientBirthDate: string;
  patientDutchInsurance: boolean;
  lastUpdated: string;
  category: ConversationCategory;
  urgent: boolean;
};

export type SignalRChatMessage = BaseConversationSignalRMessage & {
  messageId: string;
  notificationId: string;
};

export type SignalRUrgencyChangeMessage = SignalRChatMessage & {
  oldUrgence: boolean;
  newUrgence: boolean;
};

export type SignalRStatusChangeMessage = SignalRChatMessage & {
  oldStatus: ConversationStatus;
  newStatus: ConversationStatus;
};

export type SignalRAppointmentMessage = BaseConversationSignalRMessage & {
  appointmentId: string;
  startTime: string;
};

export type SignalRTemplateMessage = BaseConversationSignalRMessage & {
  startDate: string;
  endDate: string;
};

export type SignalRScheduleMessage = BaseConversationSignalRMessage & {
  startDate: string;
  endDate: string;
};

const loggingEnabled = false;

const logger = (info: string, value?: unknown) => {
  if (loggingEnabled) {
    if (value) {
      console.log(`[SIGNALR] ${info}`, value);
    } else {
      console.log(`[SIGNALR] ${info}`);
    }
  }
};

const api = `${import.meta.env.VITE_GRAPHQL_API}`.split('/graphql/')[0];

const useSignalR = () => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [message, setMessage] = useState<BaseSignalRMessage | null>(null);
  const unRegisterUrlRef = useRef<URL | null>(null);

  const [incomingMessage, setIncomingMessage] =
    useState<BaseSignalRMessage | null>(null);

  const [generateSignalRAccessToken] = useMutation(
    GenerateSignalRAccessTokenDocument
  );
  const [sendTestNotification] = useMutation(SendTestNotificationDocument);
  const [subscribeSignalRTags] = useMutation(SubscribeSignalRTagsDocument);

  const filtersRef = useRef<SignalRFilter[]>([]);

  const addFilter = useCallback((filter: SignalRFilter) => {
    const currentFilters = filtersRef.current;
    const index = currentFilters.findIndex(item => item.key === filter.key);

    // Update ref value directly
    if (index !== -1) {
      filtersRef.current = currentFilters.map((item, i) =>
        i === index ? filter : item
      );
    } else {
      filtersRef.current = [...currentFilters, filter];
    }

    logger('filters', filtersRef.current);
  }, []);

  const removeFilter = useCallback((key: string) => {
    filtersRef.current = filtersRef.current.filter(x => x.key !== key);
  }, []);

  const clearFilters = useCallback(() => {
    logger('clear filters', filtersRef.current);
    filtersRef.current = [];
    logger('filters cleared');
  }, []);

  const sendTest = useCallback(async () => {
    logger('send test notification');
    await sendTestNotification();
  }, [sendTestNotification]);

  const onUnload = useCallback(() => {
    if (unRegisterUrlRef.current) {
      logger('unregister beacon');
      navigator.sendBeacon(unRegisterUrlRef.current);
    }
  }, []);

  // window close
  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      onUnload();
    });
    return window.removeEventListener('beforeunload', () => {
      onUnload();
    });
  }, [onUnload]);

  // unique messages
  useEffect(() => {
    if (connection) {
      if (
        !message ||
        !incomingMessage?.uniqueId ||
        (message && message.uniqueId !== incomingMessage.uniqueId)
      ) {
        logger('Message', incomingMessage ?? 'no message');
        setMessage(incomingMessage);
      }
    }
  }, [connection, incomingMessage, message]);

  const onMessage = useCallback((result: BaseSignalRMessage) => {
    // always listen to loginExpired
    if (result.type === PushType.LoginExpired) {
      setIncomingMessage(result as BaseLoginExpiredSignalRMessage);
    }

    // filter messages
    if (!filtersRef.current.some(filter => filter.filter(result, 'onMessage')))
      return;

    switch (result.type) {
      case PushType.MessageSend:
      case PushType.MessageUpdated:
      case PushType.MessageReceived:
      case PushType.MessageDeleted: {
        setIncomingMessage(result as SignalRChatMessage);
        break;
      }
      case PushType.AppointmentAdded:
      case PushType.AppointmentDeleted:
      case PushType.AppointmentUpdated: {
        setIncomingMessage(result as SignalRAppointmentMessage);
        break;
      }
      case PushType.AgendaTemplateApplied: {
        setIncomingMessage(result as SignalRTemplateMessage);
        break;
      }
      case PushType.ScheduleUpdated: {
        setIncomingMessage(result as SignalRScheduleMessage);
        break;
      }
      case PushType.ConversationCreated:
      case PushType.ConversationUpdated:
      case PushType.ConversationDeleted: {
        const conversationData = result as BaseConversationSignalRMessage;
        if (conversationData?.conversation) {
          const c: ConversationSignalRMessage = JSON.parse(
            conversationData?.conversation
          );
          c.healthProviderId = conversationData.healthProviderId;
          c.conversationId = conversationData.conversationId;
          c.healthProfessionalId = conversationData.healthProfessionalId;
          c.type = conversationData.type;
          c.isSilent = conversationData.isSilent;
          c.uniqueId = conversationData.uniqueId;
          setIncomingMessage(c as ConversationSignalRMessage);
        }

        break;
      }
      default:
        break;
    }
  }, []);

  const openConnection = useCallback(
    async (url: string) => {
      if (!connection) {
        try {
          const deviceIdentifier = uuid();
          logger('start connection', deviceIdentifier);

          const deviceUnregisterUrl = new URL(
            `${api}/unregister-device/${deviceIdentifier}`
          );

          if (
            unRegisterUrlRef.current &&
            unRegisterUrlRef.current !== deviceUnregisterUrl
          ) {
            logger('unregister', unRegisterUrlRef.current);
            await fetch(unRegisterUrlRef.current);
            unRegisterUrlRef.current = null;
          }

          const {
            data: signalRAccessTokenData,
            errors: signalRAccessTokenErrors,
          } = await generateSignalRAccessToken({
            variables: { deviceIdentifier },
          });

          if (signalRAccessTokenErrors) {
            logger('errors', signalRAccessTokenErrors);
          }

          logger('deviceUnregisterUrl', deviceUnregisterUrl);
          unRegisterUrlRef.current = deviceUnregisterUrl;

          // token data
          const token =
            signalRAccessTokenData?.generateAccessToken.signalRAccessToken
              ?.token ?? null;

          const expiredUtc =
            signalRAccessTokenData?.generateAccessToken.signalRAccessToken
              ?.expiredUtc ?? null;

          logger(`device : ${deviceIdentifier} expires : ${expiredUtc}`);

          if (token) {
            const setup = new HubConnectionBuilder();
            // setup.withAutomaticReconnect();
            setup.withUrl(url, {
              accessTokenFactory: () => token,
            });

            if (loggingEnabled) {
              setup.configureLogging(LogLevel.Debug);
            }

            const currentConnection = setup.build();
            setConnection(currentConnection);

            await currentConnection.start();

            // connected
            if (currentConnection.connectionId) {
              logger(
                `connected connectionId : ${currentConnection.connectionId} deviceId : ${deviceIdentifier}`
              );

              logger('try to register tags...');

              const { data: subscribe, errors } = await subscribeSignalRTags({
                variables: {
                  deviceIdentifier,
                  connectionId: currentConnection.connectionId,
                },
              });

              if (errors && errors?.length > 0) {
                logger(errors.toString());
              }

              const registered =
                errors && errors.length > 0
                  ? false
                  : (subscribe?.subscribeTags?.boolean ?? false);

              logger(
                `tags registered: ${registered} ${
                  errors && errors.length > 0 ? `errors ${errors}` : ''
                }`
              );

              if (registered) {
                currentConnection.off('SendSilentNotification'); // remove before adding
                currentConnection.on('SendSilentNotification', onMessage);
              }
            }

            // other events
            currentConnection.onclose((error?: Error) => {
              logger(`on close ${error?.message ?? ''}`);
              setConnection(null);
            });
            currentConnection.onreconnecting((error?: Error) => {
              logger(`on reconnecting ${error?.message ?? ''}`);
            });
            currentConnection.onreconnected((connectionId?: string) => {
              logger(`on reconnected ${connectionId}`);
            });

            /*
            setInterval(() => {
              console.log('spam');
              setIncomingMessage({
                type: PushType.ConversationUpdated,
                messageId: 'e3a5fe1a-389f-4c0f-a41a-b6f5acae22e4',
                conversationId: 'facf4005-b318-4e04-b99b-54899dad9505',
                healthProviderId: 'c7e14a61-4299-d1f3-b394-8c65646f6363',
                healthProfessionalId: '12478065-19e3-d18b-9bf7-1145646f6363',
                uniqueId: (Math.random() + 1).toString(36).substring(7),
                notificationId: '1fdf8d37-0b5c-439d-a746-e4d698e1649e',
                isSilent: true,
              } as SignalRChatMessage);
            }, 50); */

            // await sendTest();
            return true;
          }
        } catch (error) {
          logger((error as Error).message);
          timeoutRef.current = setTimeout(() => {
            logger('try reconnect');
            openConnection(url);
          }, 5000);
          return false;
        }
      } else {
        logger(
          `already connected - connectionId : ${connection.connectionId} state : ${connection.state}`
        );

        return (
          connection.state === HubConnectionState.Connected ||
          connection.state === HubConnectionState.Connecting
        );
      }
      return false;
    },
    [connection, generateSignalRAccessToken, onMessage, subscribeSignalRTags]
  );

  const closeConnection = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setMessage(null);
    setIncomingMessage(null);
    // always unregister
    if (unRegisterUrlRef.current) {
      try {
        await fetch(unRegisterUrlRef.current);
      } catch (error) {
        logger('unregister error', error);
      } finally {
        unRegisterUrlRef.current = null;
      }
    }
    // close connection
    if (connection) {
      const { connectionId } = connection;
      logger(`close connection - connectionId : ${connectionId}`);
      try {
        await connection.stop();
      } catch (error) {
        logger('error closing connection', error);
      }
      connection.off('SendSilentNotification');
      logger(`closed ${connectionId}`);
      setConnection(null);
    }
  }, [connection]);

  return {
    openConnection,
    closeConnection,
    sendTest,
    message,
    connection,
    addFilter,
    removeFilter,
    clearFilters,
  };
};

export { useSignalR };
